import { connectMessagingSocket, ThreadMessage } from "./websocket";

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed";

export interface MessagingConnection {
  send(threadId: string, body: string): boolean;
  status(): ConnectionStatus;
  close(): void;
}

export interface MessagingConnectionOptions {
  onMessage: (message: ThreadMessage) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  /**
   * Fired after a reconnect, never on the first connect. Messages sent while the socket was down were
   * never pushed to this client and are not replayed by the server, so the page has to refetch to catch
   * up -- without this a reconnect looks healthy while silently missing everything from the gap.
   */
  onReconnected?: () => void;
}

/** Doubling backoff, capped. The first retry is fast because most drops are the idle timeout and reconnect immediately. */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/** Full jitter on top of the backoff, so several tabs waking together don't retry in lockstep. */
function delayFor(attempt: number): number {
  const base = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  return base / 2 + Math.random() * (base / 2);
}

/**
 * A messaging connection that survives being dropped.
 *
 * API Gateway closes an idle WebSocket after 10 minutes and hard-closes any connection at 2 hours.
 * Nothing handled that: the socket died silently, incoming messages simply stopped, and sending hit a
 * readyState guard whose message ("Still connecting — try again in a moment") was untrue, because
 * nothing was going to reconnect. The only recovery was reloading the page.
 *
 * Reconnects with jittered backoff, reports status so the UI can say what is happening, and signals a
 * successful reconnect so the caller can refetch whatever arrived while it was down.
 */
export function createMessagingConnection(options: MessagingConnectionOptions): MessagingConnection {
  let socket: WebSocket | null = null;
  let status: ConnectionStatus = "connecting";
  let attempt = 0;
  let everConnected = false;
  let closedByCaller = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function setStatus(next: ConnectionStatus) {
    if (status === next) return;
    status = next;
    options.onStatusChange?.(next);
  }

  function scheduleReconnect() {
    if (closedByCaller) return;
    setStatus("reconnecting");
    retryTimer = setTimeout(connect, delayFor(attempt));
    attempt += 1;
  }

  async function connect() {
    if (closedByCaller) return;
    try {
      const ws = await connectMessagingSocket(options.onMessage);

      // The caller may have closed while the token fetch and handshake were in flight; without this the
      // orphaned socket stays open and keeps delivering messages to a page that has moved on.
      if (closedByCaller) {
        ws.close();
        return;
      }

      socket = ws;
      ws.addEventListener("open", () => {
        attempt = 0;
        setStatus("open");
        if (everConnected) options.onReconnected?.();
        everConnected = true;
      });
      ws.addEventListener("close", () => {
        socket = null;
        scheduleReconnect();
      });
      // An error is always followed by a close event, which is what drives the retry -- handled only so
      // it isn't an unhandled error on the socket.
      ws.addEventListener("error", () => {});
    } catch {
      // The token fetch or the handshake itself failed. Same recovery: back off and try again.
      scheduleReconnect();
    }
  }

  void connect();

  return {
    send(threadId, body) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return false;
      socket.send(JSON.stringify({ action: "sendMessage", threadId, body }));
      return true;
    },
    status: () => status,
    close() {
      closedByCaller = true;
      if (retryTimer) clearTimeout(retryTimer);
      setStatus("closed");
      socket?.close();
      socket = null;
    },
  };
}
